import { ChevronDown, ChevronUp, Radio, Users } from "lucide-react";

const DISTANCE_PRESETS = [5, 25, 100, 500];

/**
 * Bottom sheet:
 *   • Toggle handle showing filtered count + live count
 *   • Radius pills + Online/Verified/Paying chips (filter drawer replacement)
 *   • Horizontal strip of avatar cards for the top matches
 *
 * Pure presentational — parent owns all filter state.
 */
export default function NearbySheet({
  open,
  onToggle,
  filtered,
  liveCount,
  busy,
  radiusMi,
  onRadius,
  onlineOnly,
  onToggleOnline,
  verifiedOnly,
  onToggleVerified,
  payingOnly,
  onTogglePaying,
  onSelect,
}) {
  const chips = [
    { key: "online", label: "Online", on: onlineOnly, toggle: onToggleOnline },
    { key: "verified", label: "Verified", on: verifiedOnly, toggle: onToggleVerified },
    { key: "paying", label: "Paying", on: payingOnly, toggle: onTogglePaying },
  ];

  return (
    <div
      className="pointer-events-auto absolute inset-x-0 bottom-0 z-[1200] transition-transform duration-300"
      style={{ transform: open ? "translateY(0)" : "translateY(calc(100% - 56px))" }}
      data-testid="nearby-sheet"
    >
      <div className="mx-auto max-w-2xl bg-zinc-950/95 backdrop-blur-md border-t border-white/10 rounded-t-3xl shadow-2xl">
        <button
          onClick={onToggle}
          data-testid="nearby-sheet-toggle"
          className="w-full flex items-center justify-between px-5 py-3 text-white"
        >
          <div className="flex items-center gap-2">
            <Users size={16} className="text-rose-500" />
            <span className="font-display text-lg">{filtered.length} people</span>
            {liveCount > 0 && (
              <span className="text-[10px] uppercase tracking-widest bg-rose-600 text-white rounded-full px-2 py-0.5 flex items-center gap-1">
                <Radio size={10} /> {liveCount} live
              </span>
            )}
          </div>
          {open ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </button>

        <div className="px-5 pb-3 flex gap-2 overflow-x-auto flex-nowrap">
          {DISTANCE_PRESETS.map((mi) => (
            <button
              key={mi}
              onClick={() => onRadius(mi)}
              data-testid={`nearby-radius-${mi}`}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border ${
                radiusMi === mi
                  ? "bg-rose-600 border-rose-600 text-white"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {mi} mi
            </button>
          ))}
          <div className="w-px bg-white/10 mx-1" />
          {chips.map((f) => (
            <button
              key={f.key}
              onClick={f.toggle}
              data-testid={`nearby-filter-${f.key}`}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border ${
                f.on
                  ? "bg-yellow-400 border-yellow-300 text-black"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="pb-4 pt-1 overflow-x-auto flex gap-3 px-5 flex-nowrap">
          {busy && filtered.length === 0 && (
            <div className="text-zinc-500 text-sm py-6">Loading pins…</div>
          )}
          {!busy && filtered.length === 0 && (
            <div className="text-zinc-500 text-sm py-6">
              No matches — try a bigger radius or turn off filters.
            </div>
          )}
          {filtered.slice(0, 30).map((p) => (
            <button
              key={p.id}
              onClick={() => onSelect(p)}
              data-testid={`nearby-strip-${p.id}`}
              className="shrink-0 w-24 text-left group"
            >
              <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-white/10">
                <img
                  src={p.avatar_url}
                  alt={p.handle}
                  className="w-full h-full object-cover group-hover:scale-105 transition"
                  onError={(e) => (e.currentTarget.style.background = "#f43f5e")}
                />
                {p.is_live && (
                  <div className="absolute top-1 left-1 text-[9px] uppercase tracking-widest bg-rose-600 text-white rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
                    <Radio size={8} /> Live
                  </div>
                )}
                {p.online_now && !p.is_live && (
                  <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-black" />
                )}
                {p.shared_desires_count > 0 && (
                  <div
                    className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest bg-rose-500/95 text-white rounded-full px-1.5 py-0.5"
                    title={`${p.shared_desires_count} shared desires with you`}
                    data-testid={`nearby-strip-shared-${p.id}`}
                  >
                    ✨ {p.shared_desires_count}
                  </div>
                )}
              </div>
              <div className="mt-1.5 text-xs text-white truncate">{p.display_name}</div>
              <div className="text-[10px] text-zinc-500">{p.distance_mi == null ? "LIVE" : p.pin_type === "fuzzy" ? `~${p.distance_mi} mi · approx` : p.distance_mi < 1 ? "< 1 mi" : `${p.distance_mi} mi`}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
